import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {MatTableDataSource} from '@angular/material/table';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionsService {

  public subscriptionLetterDataSource: MatTableDataSource<any>
  public displayedSubscriptionLetterColumns: string[] = [ 'icon', 'member_name', 'member_email', 'accept', 'sent_date', 'response_date', 'selected' ];

  constructor (
    private db: AngularFirestore
  ) { }

  async getSubscriptionsByContext (isCongress: boolean, context_id: any) {
    // crega todos os eventos con este contexto
    try {
      let eventsInContext: any;
      if (isCongress) {
        eventsInContext = await this.getEventsInContext(context_id)
      } else {
        eventsInContext = [ context_id ]
      }

      const productsByEvents = await this.getProductsByEvents(eventsInContext)
      const ordersByProducts = await this.getOrdersByProducts(productsByEvents)

      return ordersByProducts; // aka subscriptions
    } catch (error) {
      console.error("Erro getSubscriptionsByContext: ", error);
      throw error;
    }
  }

  async getEventsInContext (context_id: string): Promise<any[]> {
    const array: any[] = [];
    try {
      const querySnapshot = await this.db.collection<any>('sbccv_events', ref => ref
        .where("congress", "==", true)
        .where("context_id", "==", context_id))
        .get()
        .toPromise();

      querySnapshot.forEach((doc) => {
        // Adiciona cada documento ao array
        array.push(doc.id);
      });
      // Retorna o array após carregado
      return array;
    } catch (error) {
      console.error("Erro getEventsInContext: ", error);
      throw error;
    }
  }

  async getProductsByEvents (events: string[]): Promise<any[]> {
    // console.log("getProductsByEvents: " + JSON.stringify(events))

    const productArray: any[] = [];

    for (const event_id of events) {
      try {
        const querySnapshot = await this.db.collection('sbccv_product_itens', ref => ref
          .where('event_id', '==', event_id))
          .get()
          .toPromise();

        querySnapshot.forEach((doc: any) => {
          // console.log("doc.id: " + doc.id)
          productArray.push(doc.data().product_id);
        });

      } catch (error) {
        console.error(`Erro ao buscar produtos para o evento ${ event_id }:`, error);
      }
    }
    return productArray;
  }

  async getOrdersByProducts (products) {
    const orderArray: any[] = [];

    for (const product_id of products) {
      try {
        const querySnapshot = await this.db.collection('sbccv_orders', ref => ref
          .where('product_id', '==', product_id)
          .where('returnCode', '==', '00'))
          .get()
          .toPromise();

        querySnapshot.forEach((doc: any) => {
          //console.log("doc.id: " + doc.id)
          orderArray.push({
            'id': doc.id,
            'product_id': doc.data().product_id,
            'product_name': doc.data().product_name,
            'member_id': doc.data().member_id,
            'member_name': doc.data().member_name,
            'member_email': doc.data().member_email,
            "topic_id": doc.data().topic_id || '',
            "topic_name": doc.data().topic_name || '',
            "text1": doc.data().topic_text1 || '',
            "text2": doc.data().topic_text2 || '',
            "text3": doc.data().topic_text3 || '',
          });
        });

      } catch (error) {
        console.error(`Erro ao buscar order para o produto ${ product_id }:`, error);
      }
    }

    orderArray.sort(this.compareMembers)
    return orderArray;

  }

  async getLettersSentByContext (context_id: any, letter_id: any, subscriptions: any[]) {
    try {
      return this.db.collection<any>('sbccv_subscribers_sent', ref => ref
        .where("context_id", "==", context_id)
        .where("letter_id", "==", letter_id)
      )
        .snapshotChanges()
        .subscribe(async (result) => {
          const letters: any[] = [];
          result.forEach((item) => {
            const data = item.payload.doc.data()
            letters.push({
              'id': data.id,
              'context_id': data.context_id,
              'member_id': data.member_id,
              'member_name': data.member_name,
              'member_email': data.member_email,
              'sentDate': data.sentDate,
              'responseDate': data.responseDate,
              'letter_link': data.letter_link,
              'refuse': data.refuse
            })
          })
          this.checkLettersSent(letters, subscriptions)
        })
    } catch (error) {
      console.error("Erro getLettersSent: ", error);
      throw error;
    }
  }

  checkLettersSent (letters: any[], subscriptions: any[]) {
    this.subscriptionLetterDataSource = null;
    const array: any[] = []

    subscriptions.forEach((subscription) => {
      let entity: any = {}
      entity.product_id = subscription.product_id;
      entity.product_name = subscription.product_name;
      entity.member_id = subscription.member_id;
      entity.member_name = subscription.member_name;
      entity.member_email = subscription.member_email;

      const alreadySent = letters.find((letter) => {
        return letter.member_id == subscription.member_id
      })
      if (alreadySent) {
        entity.id = alreadySent.id;
        entity.context_id = alreadySent.context_id;
        entity.sentDate = alreadySent.sentDate.toDate();
        entity.responseDate = alreadySent.responseDate ? alreadySent.responseDate.toDate() : null;
        entity.letter_link = alreadySent.letter_link;
        entity.refuse = alreadySent.refuse;
      } else {
        entity.id = "";
        entity.context_id = "";
        entity.sentDate = null;
        entity.responseDate = null;
        entity.letter_link = "";
        entity.refuse = false
      }
      array.push(entity);
    })
    array.sort(this.compareMembers)
    this.subscriptionLetterDataSource = new MatTableDataSource(array)
  }

  compareMembers (a: any, b: any) {

    const pa = String(a.member_name)
    const pb = String(b.member_name)

    if (pa > pb) return 1;
    if (pb > pa) return -1;
    return 0;

  }

  async getMasterEvent (context: any) {
    try {
      return this.db.collection<Event>('sbccv_events', ref => ref
        .where('deleted', '==', false)
        .where('context_id', '==', context)
        .where('master', '==', true)
        .limit(1))
        .get()
        .toPromise()
        .then(async (result: any) => {
          return {
            "id": result.docs[ 0 ].id,
            "name": result.docs[ 0 ].data().name
          }
        })
    } catch (error) {
      console.error("Erro getMasterEvent: ", error);
      throw error;
    }
  }

}
